import React, { useEffect, useState } from "react";

import { Sidebar } from "@/client/components/layout/Sidebar";
import { CommandPalette } from "@/client/components/ui/CommandPalette";
import { ChatProvider, useChatContext } from "@/client/context/ChatContext";
import { DataVersionProvider } from "@/client/context/DataVersionContext";
import { ThemeProvider } from "@/client/context/ThemeContext";
import { ToastProvider } from "@/client/context/ToastContext";
import { matchRoute, useRouter } from "@/client/hooks/useRouter";
import { MenuIcon } from "@/client/icons";
import { ChatPage } from "@/client/pages/ChatPage";
import { IngredientsPage } from "@/client/pages/IngredientsPage";
import { PlanPage } from "@/client/pages/PlanPage";
import { RecipeDetailPage } from "@/client/pages/RecipeDetailPage";
import { RecipesPage } from "@/client/pages/RecipesPage";
import { SessionsPage } from "@/client/pages/SessionsPage";
import { ShoppingListPage } from "@/client/pages/ShoppingListPage";
import { StockPage } from "@/client/pages/StockPage";

function PageContent({ route, params }: { route: string; params: Record<string, string> }) {
  switch (route) {
    case "/":
    case "/c/:id":
      return <ChatPage />;
    case "/recetas":
      return <RecipesPage />;
    case "/recetas/nueva":
      return <RecipeDetailPage isNew />;
    case "/recetas/:id":
      return <RecipeDetailPage id={params.id} />;
    case "/recetas/:id/editar":
      return <RecipeDetailPage id={params.id} startInEditMode />;
    case "/ingredientes":
      return <IngredientsPage />;
    case "/ingredientes/:id":
      return <IngredientsPage initialIngredientId={params.id} />;
    case "/plan":
      return <PlanPage />;
    case "/plan/:startDate":
      return <PlanPage initialStartDate={params.startDate} />;
    case "/stock":
      return <StockPage />;
    case "/stock/:recipeId":
      return <StockPage focusRecipeId={params.recipeId} />;
    case "/compras":
      return <ShoppingListPage />;
    case "/sesiones":
      return <SessionsPage />;
    case "/sesiones/:id":
      return <SessionsPage initialSessionId={params.id} />;
    default:
      return <ChatPage />;
  }
}

function AppShell() {
  const { path, navigate } = useRouter();
  const { loadConversation } = useChatContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
      if (e.key === "j" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        navigate("/");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const { route, params } = matchRoute(path);

  // Load conversation from URL if needed
  useEffect(() => {
    if (route === "/c/:id" && params.id) {
      void loadConversation(params.id);
    }
  }, [route, params.id, loadConversation]);

  const handleNavigate = (to: string) => {
    navigate(to);
    setSidebarOpen(false);
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-term-bg">
      {/* Sidebar */}
      <Sidebar
        currentPath={path}
        onNavigate={handleNavigate}
        onOpenPalette={() => setPaletteOpen(true)}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-term-border bg-term-surface shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-term-text-dim hover:text-term-text cursor-pointer p-1 -ml-1"
            aria-label="Abrir menú"
          >
            <MenuIcon />
          </button>
          <span className="font-serif text-term-accent text-xl flex-1 text-center">Meal Prep</span>
          <div className="w-6" />
        </div>

        {/* Page */}
        <main className="flex-1 overflow-y-auto">
          <div key={route + JSON.stringify(params)} className="animate-[fade-in_0.15s_ease] h-full">
            <PageContent route={route} params={params} />
          </div>
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={handleNavigate}
      />
    </div>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <DataVersionProvider>
          <ChatProvider>
            <AppShell />
          </ChatProvider>
        </DataVersionProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
